import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
    alpha,
} from "@mui/material";
import {
    CustomAccordionComponent,
    CustomAccordionComponentDetails,
    CustomAccordionComponentSummary,
    LightTooltip,
} from "../../styled/CustomComponent";
import { forwardRef } from "react";
import { TableVirtuoso } from "react-virtuoso";
import { ValueComponent } from "./CustomTextFormatting";
import { grey } from "@mui/material/colors";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Entity, ModulePath } from "../../../constant/ApplicationVariables";
import { useApplicationContext } from "../../../context/ApplicationContext";

export function AccordionDetailsContainer({ children, ...props }) {
    const theme = useTheme();
    
    return (
        <Box 
            className="flex flex-col gap-2 max-h-48 overflow-y-auto"
            sx={{
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.background.paper, 0.5)
                        : alpha(grey[100], 0.5),
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.primary.main, 0.3)
                        : alpha(grey[400], 0.5),
                    borderRadius: '4px',
                    '&:hover': {
                        background: theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.primary.main, 0.5)
                            : alpha(grey[500], 0.7),
                    },
                },
            }}
        >
            {children}
        </Box>
    );
}
export function SectionContainer({
    children,
    dataAvailable,
    enableEdit,
    currentSection,
    ...props
}) {
    const theme = useTheme();

    const boxElement = (
        <Box
            className={`w-full flex flex-col gap-1 ${props.className}`}
            {...props}
            sx={{
                // Simple border with theme color
                border: `1px solid ${theme.palette.divider}`,
                
                // Professional padding
                padding: theme.spacing(2),
                
                // No background fill
                backgroundColor: 'transparent',
                
                // Border radius
                borderRadius: theme.shape.borderRadius,
                
                // Hover effect based on theme
                ...(props.onClick && {
                    cursor: 'pointer',
                    '&:hover': {
                        borderColor: theme.palette.mode === 'dark' 
                            ? theme.palette.divider // Same gray in dark mode
                            : theme.palette.primary.main, // Primary color in light mode
                    }
                }),
                
                // Editing state (thicker border)
                ...(enableEdit === currentSection && {
                    borderWidth: '2px',
                }),
                
                // Missing data state (dashed border)
                ...(dataAvailable === false && {
                    borderStyle: 'dashed',
                }),
                
                ...props.sx,
            }}
        >
            {children}
        </Box>
    );

    // If editing ----> return ONLY the Box (no tooltip wrapper at all)
    if (enableEdit === currentSection) {
        return boxElement;
    }

    // If NOT editing ----> only show tooltip normally
    return (
        <LightTooltip
            title={props.onClick ? "Click to edit" : ""}
            arrow
            disableHoverListener={!props.onClick}
        >
            {boxElement}
        </LightTooltip>
    );
}
export function AccordionComponent(props) {
    const theme = useTheme();
    
    return (
        <CustomAccordionComponent
            {...props}
            sx={{ 
                ...props.sx, 
                maxWidth: "400px",
                background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
            }}
        />
    );
}

export function AccordionComponentSummary(props) {
    return <CustomAccordionComponentSummary {...props} />;
}

export function AccordionComponentDetails(props) {
    const theme = useTheme();
    
    return (
        <CustomAccordionComponentDetails 
            {...props}
            sx={{
                background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.5)
                    : alpha(theme.palette.background.default, 0.7),
                ...props.sx,
            }}
        />
    );
}

export const TableComponents = {
    Scroller: forwardRef((props, ref) => {
        const theme = useTheme();
        
        return (
            <TableContainer
                component={Paper}
                style={{ overflowX: "auto", overflowY: "scroll" }}
                {...props}
                ref={ref}
                sx={{
                    background: theme.palette.background.default,
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.background.paper, 0.5)
                            : alpha(grey[100], 0.5),
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.primary.main, 0.3)
                            : alpha(grey[400], 0.5),
                        borderRadius: '4px',
                        '&:hover': {
                            background: theme.palette.mode === 'dark' 
                                ? alpha(theme.palette.primary.main, 0.5)
                                : alpha(grey[500], 0.7),
                        },
                    },
                }}
            />
        );
    }),
    Table: (props) => <Table {...props} style={{ borderCollapse: "separate" }} />,
    TableHead: TableHead,
    TableRow: (props) => {
        const theme = useTheme();
        
        return (
            <TableRow
                {...props}
                sx={{ 
                    ...props.sx, 
                    "&:hover": { 
                        background: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.08)
                            : alpha(theme.palette.primary.main, 0.04),
                    } 
                }}
            />
        );
    },
    TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
    TableCell: (props) => {
        const theme = useTheme();
        
        return (
            <TableCell 
                {...props} 
                sx={{ 
                    ...props?.sx, 
                    px: "1rem !important",
                    borderColor: theme.palette.divider,
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                }} 
            />
        );
    },
    TableFooter: (props) => {
        const theme = useTheme();
        
        return (
            <TableRow 
                {...props} 
                sx={{ 
                    ...props.sx, 
                    background: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.08)
                        : alpha(theme.palette.primary.main, 0.04),
                    '& .MuiTableCell-root': {
                        backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.08)
                            : alpha(theme.palette.primary.main, 0.04),
                    }
                }} 
            />
        );
    },
};

export function LinkedItemsTable({ list, ...props }) {
    const { handleOpenItemInNewWindow } = useApplicationContext();
    const theme = useTheme();
    const { t } = useTranslation("translation", { keyPrefix: "templateCommon" });

    const getLinkedItemUrl = (item) => {
        if (!item?.customId) return "";
        switch (item?.type) {
            case Entity.userStory:
                return `${ModulePath.requirements}?id=${item?.customId}`;
            case Entity.useCase:
                return `${ModulePath.useCase}?id=${item?.customId}`;
            case Entity.testCase:
                return `${ModulePath.testCase}?id=${item?.customId}`;
            default:
                return "";
        }
    };

    const handleClickItem = (item) => {
        if (item?.customId && item?.folderPath) {
            handleOpenItemInNewWindow(item);
        }
    };

    const getHeaderBackground = () => {
        return theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.9)
            : theme.palette.background.paper;
    };

    const getBorderColor = () => {
        return theme.palette.mode === 'dark'
            ? theme.palette.divider
            : grey[300];
    };

    const getHoverBackground = () => {
        return theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.08)
            : alpha(theme.palette.primary.main, 0.04);
    };

    // Get background color for table cells - black in dark mode, white in light mode
    const getTableCellBackground = () => {
        return theme.palette.mode === 'dark'
            ? '#000000'  // Pure black for dark mode
            : '#ffffff'; // Pure white for light mode
    };

    // Get text color that contrasts with the background
    const getContrastTextColor = (backgroundColor) => {
        if (backgroundColor === '#000000') {
            return '#ffffff'; // White text on black
        } else {
            return '#000000'; // Black text on white
        }
    };

    return (
        <TableVirtuoso
            style={{ 
                width: "100%", 
                height: "205px", 
                overflowX: "auto",
                borderRadius: theme.shape.borderRadius,
                backgroundColor: getTableCellBackground(),
            }}
            data={list}
            components={TableComponents}
            fixedHeaderContent={() => {
                const headerBg = getHeaderBackground();
                const headerTextColor = getContrastTextColor(headerBg);
                
                return (
                    <TableHead>
                        <TableCell
                            align="center"
                            padding={"none"}
                            className="whitespace-nowrap"
                            style={{
                                margin: "0 1rem !important",
                                fontWeight: "bold",
                                minWidth: "42px",
                                maxWidth: "42px",
                                background: headerBg,
                                borderRight: `1px solid ${getBorderColor()}`,
                                color: headerTextColor,
                            }}
                        >
                            <ValueComponent
                                text={t("sNoText")}
                                className="whitespace-nowrap py-1 px-2"
                                fontWeight="bold"
                                style={{ color: headerTextColor }}
                            />
                        </TableCell>

                        <TableCell
                            align="left"
                            padding={"none"}
                            className="whitespace-nowrap"
                            style={{
                                margin: "0 1rem !important",
                                fontWeight: "bold",
                                minWidth: "100px",
                                maxWidth: "100px",
                                background: headerBg,
                                borderRight: `1px solid ${getBorderColor()}`,
                                color: headerTextColor,
                            }}
                        >
                            <ValueComponent
                                text={t("idText")}
                                className="whitespace-nowrap py-1 px-2"
                                fontWeight="bold"
                                style={{ color: headerTextColor }}
                            />
                        </TableCell>

                        <TableCell
                            align="left"
                            padding={"none"}
                            className="whitespace-nowrap"
                            style={{
                                margin: "0 1rem !important",
                                fontWeight: "bold",
                                width: "100%",
                                maxWidth: "100%",
                                background: headerBg,
                                borderRight: `1px solid ${getBorderColor()}`,
                                color: headerTextColor,
                            }}
                        >
                            <ValueComponent
                                text={t("titleText")}
                                className="whitespace-nowrap py-1 px-2"
                                fontWeight="bold"
                                style={{ color: headerTextColor }}
                            />
                        </TableCell>

                        <TableCell
                            align="left"
                            padding={"none"}
                            className="whitespace-nowrap"
                            style={{
                                margin: "0 1rem !important",
                                fontWeight: "bold",
                                width: "100%",
                                maxWidth: "100%",
                                background: headerBg,
                                color: headerTextColor,
                            }}
                        >
                            <ValueComponent
                                text={"FolderPath"}
                                className="whitespace-nowrap py-1 px-2"
                                fontWeight="bold"
                                style={{ color: headerTextColor }}
                            />
                        </TableCell>
                    </TableHead>
                );
            }}
            itemContent={(index, itemData) => {
                const cellBg = getTableCellBackground();
                const cellTextColor = getContrastTextColor(cellBg);
                const isInteractive = itemData?.customId && itemData?.folderPath;
                
                return (
                    <TableRow 
                        sx={{ 
                            display: "table-row",
                            backgroundColor: cellBg,
                            '&:hover': {
                                background: getHoverBackground(),
                            },
                        }}
                    >
                        <TableCell
                            padding="none"
                            align="center"
                            sx={{
                                minWidth: "42px",
                                maxWidth: "42px",
                                borderRight: `1px solid ${getBorderColor()}`,
                                color: cellTextColor,
                                backgroundColor: cellBg,
                            }}
                        >
                            <ValueComponent 
                                text={index + 1} 
                                className="py-1"
                                style={{ color: cellTextColor }}
                            />
                        </TableCell>

                        <TableCell
                            padding="none"
                            align="left"
                            sx={{
                                minWidth: "100px",
                                maxWidth: "100px",
                                borderRight: `1px solid ${getBorderColor()}`,
                                color: cellTextColor,
                                backgroundColor: cellBg,
                                cursor: isInteractive ? 'pointer' : 'default',
                            }}
                            onClick={() => isInteractive && handleClickItem(itemData)}
                        >
                            <ValueComponent
                                text={`${itemData?.customId}`}
                                fontWeight="bold"
                                className="line-clamp-1 px-2 py-1 hover:underline"
                                style={{ 
                                    color: isInteractive 
                                        ? theme.palette.mode === 'dark' 
                                            ? '#06b6d4'  // Cyan for dark mode
                                            : '#1A73E8' // Blue for light mode
                                        : cellTextColor,
                                }}
                            />
                        </TableCell>

                        <TableCell
                            padding="none"
                            align="left"
                            sx={{
                                width: "100%",
                                maxWidth: "100%",
                                borderRight: `1px solid ${getBorderColor()}`,
                                color: cellTextColor,
                                backgroundColor: cellBg,
                            }}
                        >
                            <LightTooltip
                                title={<ValueComponent text={`${itemData?.title}`} />}
                                placement="left"
                            >
                                <ValueComponent
                                    text={`${itemData?.title}`}
                                    className="line-clamp-1 px-2 py-1"
                                    style={{ color: cellTextColor }}
                                />
                            </LightTooltip>
                        </TableCell>

                        <TableCell
                            padding="none"
                            align="left"
                            sx={{
                                width: "100%",
                                maxWidth: "100%",
                                color: theme.palette.mode === 'dark'
                                    ? alpha(cellTextColor, 0.7)  // Lighter text for folder path in dark mode
                                    : alpha('#000000', 0.6),    // Darker text for folder path in light mode
                                backgroundColor: cellBg,
                            }}
                        >
                            <LightTooltip
                                title={<ValueComponent text={`${itemData?.folderPath}`} />}
                                placement="left"
                            >
                                <ValueComponent
                                    text={`${itemData?.folderPath}`}
                                    className="line-clamp-1 px-2 py-1"
                                />
                            </LightTooltip>
                        </TableCell>
                    </TableRow>
                );
            }}
        />
    );
}